import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Upload, FileText, FolderOpen, 
  Plus, Search, Filter, Download,
  AlertTriangle 
} from 'lucide-react';

const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const DocumentManagement = ({ initialView = 'view' }) => {
  const [activeView, setActiveView] = useState(initialView);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // States for category management
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const { user } = useAuth();
  const token = localStorage.getItem('authToken');

  // Fetch categories and documents
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch categories
        const catResponse = await fetch(`${API_BASE_URL}/document-categories.php`,{
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const catData = await catResponse.json();
        
        if (catData.success) {
          setCategories(catData.data);
        }

        // Fetch documents
        const docResponse = await fetch(
          `${API_BASE_URL}/documents.php?search=${searchTerm}&category=${selectedCategory}`,{
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        const docData = await docResponse.json();

        if (docData.success) {
          setDocuments(docData.data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchTerm, selectedCategory]);

  // File upload handler
  const handleFileUpload = async (file) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Only PDF, Word, and Excel files are allowed.');
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setUploadError('File is too large. Maximum size is 10MB.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);
      formData.append('category_id', selectedCategory || '1'); // Default to first category if none selected
      formData.append('user_id', user.id); // TODO: Replace with actual user ID



      const response = await fetch(`${API_BASE_URL}/documents.php`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setUploadProgress(100);
        // Refresh documents list
        fetchDocuments();
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        throw new Error(data.error || 'Failed to upload file');
      }
    } catch (err) {
      setUploadError(err.message);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/documents.php?search=${searchTerm}&category=${selectedCategory}`,{
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();

      if (data.success) {
        setDocuments(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch documents');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Download handler
  const handleDownload = async (documentCode, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/documents.php?id=${documentCode}&download=true`,{
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download document');
    }
  };
  // Category management handlers
  const handleAddCategory = async (name, description) => {
    try {
      const response = await fetch(`${API_BASE_URL}/document-categories.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, description }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh categories
        const catResponse = await fetch(`${API_BASE_URL}/document-categories.php`);
        const catData = await catResponse.json();
        
        if (catData.success) {
          setCategories(catData.data);
        }
      } else {
        throw new Error(data.error || 'Failed to add category');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditCategory = async (id, name, description) => {
    try {
      const response = await fetch(`${API_BASE_URL}/document-categories.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id, name, description }),
      });

      const data = await response.json();

      if (data.success) {
        // Refresh categories
        const catResponse = await fetch(`${API_BASE_URL}/document-categories.php`);
        const catData = await catResponse.json();
        
        if (catData.success) {
          setCategories(catData.data);
        }
      } else {
        throw new Error(data.error || 'Failed to update category');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/document-categories.php?id=${id}`, {
        method: 'DELETE',
        headers: {      
           'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (data.success) {
        // Refresh categories
        const catResponse = await fetch(`${API_BASE_URL}/document-categories.php`);
        const catData = await catResponse.json();
        
        if (catData.success) {
          setCategories(catData.data);
        }
      } else {
        throw new Error(data.error || 'Failed to delete category');
      }
    } catch (err) {
      setError(err.message);
    }
  };
  const renderUploadView = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        {uploadError && (
          <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 text-red-700">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              <p>{uploadError}</p>
            </div>
          </div>
        )}

        <div 
          className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center 
            ${isUploading ? 'opacity-50' : ''} 
            ${uploadError ? 'border-red-300' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const file = e.dataTransfer.files[0];
            if (file) {
              handleFileUpload(file);
            }
          }}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
            >
              Upload a file
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(file);
                }
              }}
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            />
            <span className="text-gray-500"> or drag and drop</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            PDF, Word, Excel files up to 10MB
          </p>

          {isUploading && (
            <div className="mt-4">
              <div className="h-2 bg-gray-200 rounded-full">
                <div 
                  className="h-2 bg-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {uploadProgress < 100 ? 'Uploading...' : 'Upload complete!'} {uploadProgress}%
              </p>
            </div>
          )}
        </div>

        {/* Category Selection */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Category
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select a category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-4">Recent Uploads</h3>
        <div className="space-y-2">
          {documents
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5)
            .map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-gray-400 mr-2" />
                  <div>
                    <div className="font-medium">{doc.title}</div>
                    <div className="text-sm text-gray-500">
                      {doc.uploaded_by_name} • {new Date(doc.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <span className="text-sm text-gray-500">
                  {Math.round(doc.file_size / 1024)} KB
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );


  const renderCategoriesView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium">Document Categories</h2>
        <button 
          onClick={() => setIsAddingCategory(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </button>
      </div>

      {/* Add Category Form */}
      {isAddingCategory && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Add New Category</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setIsAddingCategory(false);
                  setNewCategory({ name: '', description: '' });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleAddCategory(newCategory.name, newCategory.description);
                  setIsAddingCategory(false);
                  setNewCategory({ name: '', description: '' });
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map(category => (
          <div key={category.id} className="bg-white rounded-lg shadow p-4">
            {editingCategory?.id === category.id ? (
              <div className="space-y-4">
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <textarea
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows="2"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setEditingCategory(null)}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleEditCategory(
                        editingCategory.id,
                        editingCategory.name,
                        editingCategory.description
                      );
                      setEditingCategory(null);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">{category.name}</h3>
                    <span className="text-sm text-gray-500">{category.document_count} documents</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingCategory(category)}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-600">{category.description}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );


  const renderMainView = () => (
    <div className="space-y-6">
      <div className="flex space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search documents..."
            className="pl-10 pr-4 py-2 w-full border rounded-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="px-4 py-2 border rounded-md"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="space-y-4">
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No documents found
                </div>
              ) : (
                documents.map(doc => (
                  <div key={doc.document_code} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="font-medium">{doc.title}</div>
                        <div className="text-sm text-gray-500">
                          {doc.category_name} • {new Date(doc.created_at).toLocaleDateString()} • 
                          {Math.round(doc.file_size / 1024)} KB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(doc.document_code, doc.file_name)}
                      className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Document Management</h1>
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveView('view')}
            className={`px-4 py-2 rounded-md ${
              activeView === 'view' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            View Documents
          </button>
          <button
            onClick={() => setActiveView('upload')}
            className={`px-4 py-2 rounded-md ${
              activeView === 'upload' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Upload Documents
          </button>
          <button
            onClick={() => setActiveView('categories')}
            className={`px-4 py-2 rounded-md ${
              activeView === 'categories' 
                ? 'bg-blue-100 text-blue-700' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Categories
          </button>
        </div>
      </div>

      {/* Content */}
      {activeView === 'upload' && renderUploadView()}
      {activeView === 'categories' && renderCategoriesView()}
      {activeView === 'view' && renderMainView()}
    </div>
  );
};

export default DocumentManagement;
